<template>
  <div class="permissions-tab">
    <div class="rbac-toolbar">
      <ZSButton color="orange" icon="Refresh" @click="refreshAll">{{ t('system.rbac.refresh') }}</ZSButton>
    </div>

    <div class="rbac-layout">
      <section v-if="canAccessProfilePermissions" class="rbac-panel permissions-panel">
        <div class="rbac-panel-header">
          <div>
            <h3>{{ t('system.rbac.permissions_title') }}</h3>
            <p class="rbac-panel-hint">{{ t('system.rbac.global_toggle_hint') }}</p>
          </div>
          <div class="rbac-global-toggle">
            <span :class="['permission-status-label', rbacEnabled ? 'enabled' : 'disabled']">
              {{ t('system.rbac.permissions_title') }}
            </span>
            <ZSSwitch :modelValue="rbacEnabled" @update:modelValue="toggleRBAC" />
          </div>
        </div>
      </section>

      <section class="role-panel">
        <div class="rbac-panel-header">
          <h3>{{ t('system.rbac.roles_title') }}</h3>
          <ZSButton color="orange" icon="Plus" @click="openRoleForm()">{{ t('system.rbac.new_role') }}</ZSButton>
        </div>
        <ZSGrid
          class="rbac-grid"
          :data="roleRows"
          :cols="roleColumns"
          :total-items="roleRows.length"
          :current-page="rolesCurrentPage"
          :items-per-page="4"
          item-key="id"
          :busy="rbacStore.busy"
          :detail="false"
          paginator
          self-paginate
          @page-changed="onRolesPageChanged"
        >
          <template #options="{ item }">
            <div class="rbac-actions">
              <button class="icon-button" type="button" @click="openRoleForm(item)"><ZSIcon icon="Pencil" /></button>
              <button class="icon-button" type="button" @click="editRolePermissions(item)"><ZSIcon icon="Key" /></button>
              <button class="icon-button" type="button" @click="editRoleTags(item)"><ZSIcon icon="Tag" /></button>
              <button class="icon-button" type="button" @click="removeRole(item)"><ZSIcon icon="Trash" /></button>
            </div>
          </template>
        </ZSGrid>
      </section>
    </div>

    <ZSDialog ref="dialogRef" />

    <ZSDialog ref="roleDialogRef" :title="roleDialogTitle" width="min(560px, 100vw)" hideButtons>
      <div class="dialog-form">
        <ZSInput :label="t('system.rbac.fields.name')" v-model="roleForm.name" />
        <ZSTextArea :label="t('system.rbac.fields.description')" v-model="roleForm.description" rows="5" />
        <template v-if="!roleForm.id">
          <ZSSelect
            :label="t('system.rbac.role_template')"
            :placeholder="t('system.rbac.role_template_placeholder')"
            :items="roleTemplateOptions"
            itemLabel="name"
            itemValue="id"
            v-model="selectedTemplateRoleId"
            clear
          />
          <div v-if="selectedTemplateRoleName" class="dialog-info template-info">
            {{ t('system.rbac.role_template_hint', { name: selectedTemplateRoleName }) }}
          </div>
        </template>
        <div class="dialog-actions">
          <ZSButton class="text" color="danger" @click="closeRoleDialog">{{ t('general.cancel') }}</ZSButton>
          <ZSButton color="orange" @click="saveRole">{{ t('general.save') }}</ZSButton>
        </div>
      </div>
    </ZSDialog>

    <ZSDialog ref="rolePermissionsDialogRef" :title="t('system.rbac.assign_permissions')" width="min(900px, 100vw)" height="min(80vh, 760px)" hideButtons>
      <div class="dialog-form dialog-form--permissions">
        <div class="dialog-info">{{ t('system.rbac.assign_permissions_to_role', { name: selectedRole?.name || '' }) }}</div>
        <div class="permission-tree-toolbar">
          <ZSButton color="warning" size="small" @click="expandPermissionTree">[+]</ZSButton>
          <ZSButton color="warning" size="small" @click="collapsePermissionTree">[-]</ZSButton>
        </div>
        <div class="permission-tree-shell">
          <ZSTree
            ref="permissionTreeRef"
            :data="permissionTree"
            item-key="id"
            item-label="label"
            item-value="value"
            item-children="children"
            v-model="selectedPermissionNames"
            selectable
            multiselect
            showCheckboxes
            border
            initially-expanded
            :isSelectable="(node) => !node.children?.length"
            :title="t('system.rbac.permissions_title')"
          >
            <template #expander="{ hasChildren, isExpanded }">
              <span v-if="hasChildren" class="permission-tree-expander">{{ isExpanded ? '[-]' : '[+]' }}</span>
            </template>
            <template #default="{ node }">
              <div :class="['permission-tree-node', { 'is-disabled': !node.children?.length && node.enabled === false }]">
                <div class="permission-tree-label-row">
                  <ZSIcon
                    v-if="!node.children?.length"
                    icon="Key"
                    size="small"
                    :color="getPermissionIconColor(node)"
                  />
                  <span>{{ getPermissionNodeLabel(node) }}</span>
                  <ZSChip v-if="!node.children?.length && node.enabled === false" :label="t('system.rbac.permission_disabled')" type="warning" />
                </div>
                <span
                  v-if="!node.children?.length && node.description"
                  :class="['permission-tree-description', { disabled: node.enabled === false }]"
                >
                  {{ node.description }}
                </span>
              </div>
            </template>
          </ZSTree>
        </div>
        <div class="dialog-actions">
          <ZSButton class="text" color="danger" @click="closeRolePermissionsDialog">{{ t('general.cancel') }}</ZSButton>
          <ZSButton color="orange" @click="saveRolePermissions">{{ t('general.save') }}</ZSButton>
        </div>
      </div>
    </ZSDialog>

    <ZSDialog ref="roleTagsDialogRef" :title="t('system.rbac.assign_groups')" width="min(560px, 100vw)" height="min(70vh, 480px)" hideButtons>
      <div class="dialog-form role-tags-form">
        <div class="dialog-info">{{ t('system.rbac.assign_groups_to_role', { name: selectedRole?.name || '' }) }}</div>
        <div class="dialog-info dialog-info--muted">{{ t('system.rbac.no_groups_hint') }}</div>
        <ZSMultiSelect
          :label="t('system.rbac.assign_groups')"
          v-model="selectedTagIds"
          :items="tagStore.tags"
          itemLabel="name"
          itemValue="id"
          max-height="260px"
          clear
        />
        <div class="dialog-actions role-tags-actions">
          <ZSButton class="text" color="danger" @click="closeRoleTagsDialog">{{ t('general.cancel') }}</ZSButton>
          <ZSButton color="orange" @click="saveRoleTags">{{ t('general.save') }}</ZSButton>
        </div>
      </div>
    </ZSDialog>

  </div>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { storeToRefs } from "pinia";
import { useI18n } from 'vue-i18n';
import { useUserStore } from "@/stores/user";
import { useRBACStore } from '@/stores/rbac';
import { useTagStore } from '@/stores/tag';
import { useZSDialog } from '@/components/zs-components/overlay/useZSDialog';

const userStore = useUserStore();
const { user } = storeToRefs(userStore);

const { t, te } = useI18n();
const rbacStore = useRBACStore();
const tagStore = useTagStore();
const { dialogRef, show } = useZSDialog();

const permissionTreeRef = ref(null);
const roleDialogRef = ref(null);
const rolePermissionsDialogRef = ref(null);
const roleTagsDialogRef = ref(null);
const selectedRole = ref(null);
const rolesCurrentPage = ref(1);
const showPermissionsPanel = true;

const roleForm = ref({ id: null, name: '', description: '' });
const selectedTemplateRoleId = ref(null);
const selectedPermissionIds = ref([]);
const selectedPermissionNames = ref([]);
const selectedTagIds = ref([]);

const roleRows = computed(() => rbacStore.roles);
const roleTemplateOptions = computed(() => roleRows.value.filter((role) => role.id !== roleForm.value.id));
const permissionRows = computed(() => rbacStore.permissions);
const rbacEnabled = computed(() => rbacStore.settings?.enabled === true);
const roleDialogTitle = computed(() => roleForm.value.id ? t('system.rbac.edit_role') : t('system.rbac.new_role'));
const selectedTemplateRoleName = computed(() => roleRows.value.find((role) => role.id === selectedTemplateRoleId.value)?.name || '');
const permissionTree = computed(() => buildPermissionTree(permissionRows.value.filter((permission) => permission.enabled !== false)));

const canAccessProfilePermissions = computed(() => {
    return user.value?.master === true;
});

const roleColumns = computed(() => [
  { field: 'name', label: t('system.rbac.fields.name'), style: { width: '30%' } },
  { field: 'description', label: t('system.rbac.fields.description'), style: { width: '45%' } },
  { field: 'options', label: t('system.rbac.fields.options'), style: { width: '25%' } },
]);

function resetDialogState() {
  selectedTemplateRoleId.value = null;
}

function closeRoleDialog() {
  roleDialogRef.value?.hide();
  selectedTemplateRoleId.value = null;
}

function closeRolePermissionsDialog() {
  rolePermissionsDialogRef.value?.hide();
}

function editRoleTags(role) {
  selectedRole.value = role;
  selectedTagIds.value = (rbacStore.roleTags[role.id] || []).slice();
  roleTagsDialogRef.value?.show();
}

function closeRoleTagsDialog() {
  roleTagsDialogRef.value?.hide();
}

async function saveRoleTags() {
  await rbacStore.saveRoleTags(selectedRole.value.id, selectedTagIds.value);
  closeRoleTagsDialog();
}

function openRoleForm(role = null) {
  roleForm.value = role ? { id: role.id, name: role.name, description: role.description || '' } : { id: null, name: '', description: '' };
  selectedTemplateRoleId.value = null;
  roleDialogRef.value?.show();
}

function editRolePermissions(role) {
  selectedRole.value = role;
  selectedPermissionIds.value = (rbacStore.rolePermissions[role.id] || []).map((permission) => permission.id);
  selectedPermissionNames.value = (rbacStore.rolePermissions[role.id] || []).map((permission) => permission.name);
  rolePermissionsDialogRef.value?.show();
}

async function refreshAll() {
  await rbacStore.refreshAll();
}

async function saveRole() {
  if (!roleForm.value.name.trim()) {
    show({ type: 'error', title: t('dialog.error'), message: t('validation.required') });
    return;
  }

  if (roleForm.value.id) {
    await rbacStore.updateRole(roleForm.value.id, roleForm.value);
  } else {
    const templatePermissionIds = selectedTemplateRoleId.value
      ? (rbacStore.rolePermissions[selectedTemplateRoleId.value] || []).map((permission) => permission.id)
      : [];
    const createdRole = await rbacStore.createRole(roleForm.value);

    if (createdRole?.id && templatePermissionIds.length) {
      await rbacStore.saveRolePermissions(createdRole.id, templatePermissionIds);
    }
  }

  closeRoleDialog();
}

async function toggleRBAC(enabled) {
  await rbacStore.updateSettings({ enabled });
}

async function saveRolePermissions() {
  selectedPermissionIds.value = permissionRows.value
    .filter((permission) => permission.enabled !== false && selectedPermissionNames.value.includes(permission.name))
    .map((permission) => permission.id);

  await rbacStore.saveRolePermissions(selectedRole.value.id, selectedPermissionIds.value);
  closeRolePermissionsDialog();
}

function buildPermissionTree(permissions) {
  const roots = [];

  permissions.forEach((permission) => {
    const segments = permission.name.split('.').filter(Boolean);
    let currentLevel = roots;
    let currentPath = '';

    segments.forEach((segment, index) => {
      currentPath = currentPath ? `${currentPath}.${segment}` : segment;

      let node = currentLevel.find((item) => item.segment === segment);

      if (!node) {
        node = {
          id: currentPath,
          label: segment,
          segment,
          path: currentPath,
          value: currentPath,
          enabled: true,
          description: '',
          children: [],
        };
        currentLevel.push(node);
      }

      if (index === segments.length - 1) {
        node.value = permission.name;
        node.enabled = permission.enabled !== false;
        node.description = permission.description || '';
      }

      currentLevel = node.children;
    });
  });

  return roots;
}

function getPermissionTranslationKey(path, isLeaf) {
  if (!path) {
    return '';
  }

  return `system.rbac.permission_labels.${path}${isLeaf ? '' : '._label'}`;
}

function getPermissionNodeLabel(node) {
  const fallback = node?.segment || node?.label || node?.value || '';
  const translationKey = getPermissionTranslationKey(node?.path || node?.value, !node?.children?.length);

  return translationKey && te(translationKey) ? t(translationKey) : fallback;
}

function getPermissionIconColor(node) {
  if (selectedPermissionNames.value.includes(node?.value)) {
    return 'var(--color-orange)';
  }

  if (!node?.children?.length && node?.enabled === false) {
    return 'var(--color-gray-icon)';
  }

  return '';
}

function expandPermissionTree() {
  permissionTreeRef.value?.expandAll();
}

function collapsePermissionTree() {
  permissionTreeRef.value?.collapseAll();
}

function onRolesPageChanged(pageData) {
  rolesCurrentPage.value = typeof pageData === 'object' ? pageData.page : pageData;
}

function escapeHtml(value) {
  return String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function getUsersAssignedToRole(role) {
  const users = rbacStore.users || [];

  if (!role?.name) {
    return [];
  }

  return users.filter((user) => Array.isArray(user.roles) && user.roles.includes(role.name));
}

async function removeRole(role) {
  const assignedUsers = getUsersAssignedToRole(role);
  const assignedUsersMarkup = assignedUsers.length
    ? `<p>${escapeHtml(t('system.rbac.delete_role_assigned_users', { count: assignedUsers.length }))}</p><p><strong>${assignedUsers.map((user) => escapeHtml(user.nome || user.login || user.email || user.codigo)).join(', ')}</strong></p>`
    : `<p>${escapeHtml(t('system.rbac.delete_role_no_users'))}</p>`;

  show({
    type: 'warning',
    title: t('system.rbac.delete_role_title'),
    message: `<p>${escapeHtml(t('system.rbac.delete_role_confirm', { name: role.name }))}</p>${assignedUsersMarkup}`,
    confirmLabel: t('dialog.confirm'),
    cancelLabel: t('dialog.cancel'),
    confirmCallback: async () => {
      await rbacStore.deleteRole(role.id);
    },
  });
}

onMounted(async () => {
  resetDialogState();
  await Promise.all([refreshAll(), tagStore.fetchTags()]);
});
</script>

<style scoped>
.permissions-tab {
  padding: 0 0 1rem;
}

.rbac-layout {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.role-panel {
  width: 100%;
}

.permissions-panel {
  width: 100%;
}

.rbac-panel {
  background: #ffffff;
  border: 1px solid #d9e3ec;
  border-radius: 20px;
  padding: 1.25rem;
  box-shadow: 0 18px 40px rgba(50, 99, 141, 0.08);
}

.rbac-panel-header h3 {
  margin: 0;
  color: var(--color-dark-blue);
}

.rbac-panel-hint {
  margin: 0.35rem 0 0;
  color: var(--color-gray-icon);
  font-size: 0.9rem;
}

.rbac-panel-header,
.rbac-toolbar,
.dialog-actions,
.rbac-actions,
.rbac-chip-list {
  display: flex;
}

.rbac-panel-header,
.rbac-toolbar,
.dialog-actions {
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.role-tags-form {
  flex: 1;
}

.role-tags-actions {
  position: relative;
  z-index: 1001;
  margin-top: auto;
}


.rbac-toolbar {
  padding: 0.25rem 0 0.5rem;
  justify-content: flex-end;
}

.rbac-panel-header {
  padding: 0 0.25rem 0.85rem;
}

.users-search {
  width: min(100%, 320px);
}

.dialog-form {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.dialog-form--permissions {
  min-height: min(68vh, 640px);
}

.dialog-info {
  color: var(--color-dark-blue);
  font-size: 0.95rem;
}

.dialog-info--muted {
  color: var(--color-gray-icon);
  font-size: 0.9rem;
}

.template-info {
  margin-top: -0.25rem;
}

.rbac-global-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.permission-status-label {
  font-size: 0.82rem;
  font-weight: 600;
}

.permission-status-label.enabled {
  color: #15803d;
}

.permission-status-label.disabled {
  color: #b45309;
}

.permission-tree-shell {
  display: flex;
  flex: 1;
  min-height: 0;
  border: 1px solid #e4ebf2;
  border-radius: 16px;
  overflow: hidden;
}

.permission-tree-toolbar {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

.permission-tree-node {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.permission-tree-node.is-disabled {
  opacity: 0.8;
}

.permission-tree-label-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.permission-tree-description {
  color: var(--color-gray-icon);
  font-size: 0.8rem;
  overflow: hidden;
  text-overflow: ellipsis;
}

.permission-tree-description.disabled {
  text-decoration: line-through;
}

:deep(.permission-tree-shell .zs-tree-container) {
  display: flex;
  flex-direction: column;
  height: min(48vh, 420px);
  width: 100%;
  min-height: 0;
  overflow-y: auto;
}

:deep(.permission-tree-shell .zs-tree) {
  flex: 1 1 auto;
  min-height: 0;
  height: 100%;
  overflow-y: auto;
}

.permission-tree-expander {
  color: var(--color-orange);
  font-weight: 600;
}

:deep(.permission-tree-shell .zs-tree-node.selected) {
  background-color: transparent;
  color: var(--color-orange);
}

:deep(.permission-tree-shell .zs-tree-node.selected .permission-tree-description) {
  color: var(--color-orange);
}

:deep(.permission-tree-shell .zs-tree-node.selected .zs-tree-icon),
:deep(.permission-tree-shell .zs-tree-node.selected .zs-tree-expander) {
  color: var(--color-orange);
}

.rbac-actions,
.rbac-chip-list {
  gap: 0.5rem;
  flex-wrap: wrap;
}

.rbac-actions {
  justify-content: flex-end;
  width: 100%;
  padding: 0.25rem 0.35rem;
}

.rbac-chip-list {
  align-items: center;
}

.icon-button {
  background: rgba(235, 118, 41, 0.12);
  border: 1px solid rgba(235, 118, 41, 0.3);
  color: var(--color-dark-blue);
  border-radius: 10px;
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.icon-button:hover {
  background: rgba(235, 118, 41, 0.18);
}

:deep(.rbac-grid) {
  border: 1px solid #e4ebf2;
  border-radius: 16px;
  overflow: hidden;
  background: #ffffff;
}

:deep(.rbac-grid table) {
  background: #ffffff;
}

:deep(.rbac-grid th) {
  background: #f8fafc;
}

:deep(.rbac-grid .filter-list) {
  padding: 0 1rem 0.75rem;
}

@media (max-width: 900px) {
  .rbac-panel {
    margin-top: 0;
  }

  .rbac-panel-header,
  .dialog-actions {
    align-items: stretch;
    flex-direction: column;
  }

  :deep(.rbac-grid td) {
    padding: 0.9rem 1rem 0.9rem calc(46% + 1rem);
    text-align: left;
  }

  :deep(.rbac-grid td::before) {
    width: calc(46% - 1.5rem);
  }

  .rbac-actions,
  .rbac-chip-list {
    justify-content: flex-start;
  }
}
</style>
